import { AlertTriangle, Bell, Plus, Save, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { SeverityBadge } from "@/components/shared/SeverityBadge";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { usePolling } from "@/hooks/usePolling";
import type { Alert, AlertRule, AlertSeverity } from "@/types/metrics";

// Backend uses ">" | "<" for operator, frontend type uses "gt" etc.
type BackendRule = {
  id: string;
  name: string;
  metric: string;
  operator: ">" | "<";
  threshold: number;
  windowMs: number;
  severity: "info" | "warn" | "critical";
  enabled: boolean;
};

type BackendAlert = {
  id: string;
  ruleId: string;
  severity: string;
  message: string;
  value: number;
  threshold: number;
  triggeredAt: number;
  resolvedAt?: number;
};

type AlertsData = {
  alerts: BackendAlert[];
  rules: BackendRule[];
};

const METRICS = ["memory.percent", "cost.daily", "error.rate"];
const SEVERITIES: AlertSeverity[] = ["info", "warn", "critical"];

function emptyRule(): BackendRule {
  return {
    id: `rule-${Date.now()}`,
    name: "New Rule",
    metric: "memory.percent",
    operator: ">",
    threshold: 80,
    windowMs: 0,
    severity: "warn",
    enabled: true,
  };
}

export function Alerts() {
  const { rpc, state } = useGatewayRpc();
  const [editingRules, setEditingRules] = useState<BackendRule[] | null>(null);
  const [saving, setSaving] = useState(false);

  const fetcher = useCallback(async (): Promise<AlertsData> => {
    const [alertsRes, rulesRes] = await Promise.all([
      rpc<{ alerts: BackendAlert[] }>("metrics.alerts"),
      rpc<{ rules: BackendRule[] }>("metrics.alerts.rules"),
    ]);
    return { alerts: alertsRes.alerts, rules: rulesRes.rules };
  }, [rpc]);

  const { data, refresh } = usePolling(fetcher, 10_000, { enabled: state === "connected" });

  const activeAlerts = (data?.alerts ?? []).filter((a) => !a.resolvedAt);
  const resolvedAlerts = (data?.alerts ?? []).filter((a) => a.resolvedAt);
  const rules = editingRules ?? data?.rules ?? [];

  const startEditing = () => {
    setEditingRules([...(data?.rules ?? [])]);
  };

  const updateRule = (idx: number, patch: Partial<BackendRule>) => {
    if (!editingRules) {return;}
    const updated = [...editingRules];
    updated[idx] = { ...updated[idx], ...patch };
    setEditingRules(updated);
  };

  const removeRule = (idx: number) => {
    if (!editingRules) {return;}
    setEditingRules(editingRules.filter((_, i) => i !== idx));
  };

  const addRule = () => {
    setEditingRules([...(editingRules ?? []), emptyRule()]);
  };

  const saveRules = async () => {
    if (!editingRules) {return;}
    setSaving(true);
    try {
      await rpc("metrics.alerts.rules.update", { rules: editingRules });
      setEditingRules(null);
      refresh();
    } catch (err) {
      console.error("Failed to save rules:", err);
    } finally {
      setSaving(false);
    }
  };

  if (state !== "connected" && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold">Alerts</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {state === "connecting" ? "Connecting to gateway..." : "Gateway disconnected"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Alerts</h1>

      {/* Active Alerts */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <AlertTriangle className="h-4 w-4" /> Active Alerts ({activeAlerts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeAlerts.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No active alerts</p>
          ) : (
            <div className="space-y-2">
              {activeAlerts.map((alert) => (
                <div key={alert.id} className="flex items-start gap-3 rounded-md border p-3 dark:border-slate-800">
                  <SeverityBadge severity={alert.severity as AlertSeverity} />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{alert.message}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Value: {alert.value.toFixed(2)} | Threshold: {alert.threshold} | Triggered: {new Date(alert.triggeredAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resolved Alerts */}
      {resolvedAlerts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Recently Resolved</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 dark:border-slate-700">
                  <th className="pb-2 pr-4">Severity</th>
                  <th className="pb-2 pr-4">Message</th>
                  <th className="pb-2 pr-4">Triggered</th>
                  <th className="pb-2">Resolved</th>
                </tr>
              </thead>
              <tbody>
                {resolvedAlerts.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-2 pr-4"><SeverityBadge severity={a.severity as AlertSeverity} /></td>
                    <td className="py-2 pr-4 text-xs">{a.message}</td>
                    <td className="py-2 pr-4 text-xs text-muted-foreground">{new Date(a.triggeredAt).toLocaleTimeString()}</td>
                    <td className="py-2 text-xs text-muted-foreground">{a.resolvedAt ? new Date(a.resolvedAt).toLocaleTimeString() : "--"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Alert Rules */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Bell className="h-4 w-4" /> Alert Rules ({rules.length})
          </CardTitle>
          <div className="flex gap-2">
            {editingRules ? (
              <>
                <button
                  type="button"
                  onClick={addRule}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
                >
                  <Plus className="h-3 w-3" /> Add
                </button>
                <button
                  type="button"
                  onClick={saveRules}
                  disabled={saving}
                  className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Save className="h-3 w-3" /> {saving ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingRules(null)}
                  className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={startEditing}
                className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                Edit Rules
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {rules.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No alert rules configured</p>
          ) : (
            <div className="space-y-2">
              {rules.map((rule, idx) => (
                <div key={rule.id} className="flex items-center gap-3 rounded-md border p-2.5 text-sm dark:border-slate-800">
                  {editingRules ? (
                    <>
                      <input
                        value={rule.name}
                        onChange={(e) => updateRule(idx, { name: e.target.value })}
                        className="w-32 rounded border border-slate-200 bg-transparent px-2 py-0.5 text-xs dark:border-slate-700"
                      />
                      <select
                        value={rule.metric}
                        onChange={(e) => updateRule(idx, { metric: e.target.value })}
                        className="rounded border border-slate-200 bg-transparent px-1 py-0.5 text-xs dark:border-slate-700"
                      >
                        {METRICS.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <select
                        value={rule.operator}
                        onChange={(e) => updateRule(idx, { operator: e.target.value as ">" | "<" })}
                        className="w-12 rounded border border-slate-200 bg-transparent px-1 py-0.5 text-xs dark:border-slate-700"
                      >
                        <option value=">">&gt;</option>
                        <option value="<">&lt;</option>
                      </select>
                      <input
                        type="number"
                        value={rule.threshold}
                        onChange={(e) => updateRule(idx, { threshold: Number(e.target.value) })}
                        className="w-16 rounded border border-slate-200 bg-transparent px-2 py-0.5 text-xs dark:border-slate-700"
                      />
                      <select
                        value={rule.severity}
                        onChange={(e) => updateRule(idx, { severity: e.target.value as AlertSeverity })}
                        className="rounded border border-slate-200 bg-transparent px-1 py-0.5 text-xs dark:border-slate-700"
                      >
                        {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <label className="flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          checked={rule.enabled}
                          onChange={(e) => updateRule(idx, { enabled: e.target.checked })}
                          className="rounded"
                        />
                        On
                      </label>
                      <button type="button" onClick={() => removeRule(idx)} className="ml-auto text-red-400 hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <SeverityBadge severity={rule.severity as AlertSeverity} />
                      <span className="font-medium">{rule.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {rule.metric} {rule.operator} {rule.threshold}
                      </span>
                      <Badge variant={rule.enabled ? "default" : "secondary"} className="ml-auto text-[10px]">
                        {rule.enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
